import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import { Megaphone, RefreshCw, Loader2, Plus, Pencil, Play, Pause, Eye, MousePointerClick, FileBarChart } from 'lucide-react';

// iter378 (v14) — Direct Sponsorship Campaign manager.
// Payments happen OUTSIDE Wassla: payment_status + agreed_price are
// internal accounting notes only. This page never exposes any checkout.

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  ad_id: '', advertiser_name: '', placement: 'homepage',
  starts_at: '', sponsored_until: '', category: '', country: '', city: '',
  payment_status: 'pending', agreed_price: '', currency: 'SDG', notes: '',
};

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-sky-100 text-sky-700',
  paused: 'bg-amber-100 text-amber-700',
  expired: 'bg-slate-200 text-slate-500',
};

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminSponsorships() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const isRTL = lang === 'ar';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', form, id }
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/sponsorships?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setRows(data.sponsorships || []);
      else toast.error(data.detail || 'Failed to load');
    } catch {
      toast.error(isRTL ? 'خطأ في الشبكة' : 'Network error');
    } finally {
      setLoading(false);
    }
  }, [token, filter, isRTL]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ mode: 'create', form: { ...EMPTY_FORM } });
  const openEdit = (r) => setModal({
    mode: 'edit',
    id: r.sponsorship_id,
    form: {
      ad_id: r.ad_id, advertiser_name: r.advertiser_name, placement: r.placement,
      starts_at: toLocalInput(r.starts_at), sponsored_until: toLocalInput(r.sponsored_until),
      category: r.category || '', country: r.country || '', city: r.city || '',
      payment_status: r.payment_status, agreed_price: r.agreed_price ?? '',
      currency: r.currency || 'SDG', notes: r.notes || '',
    },
  });

  const setF = (k, v) => setModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));

  const save = async () => {
    const f = modal.form;
    if (!f.advertiser_name.trim()) return toast.error(isRTL ? 'اسم المعلن مطلوب' : 'Advertiser name is required');
    if (modal.mode === 'create' && !f.ad_id.trim()) return toast.error(isRTL ? 'معرّف الإعلان مطلوب' : 'Ad ID is required');
    if (!f.sponsored_until) return toast.error(isRTL ? 'تاريخ الانتهاء مطلوب' : 'Expiry date is required');

    const payload = {
      advertiser_name: f.advertiser_name.trim(),
      placement: f.placement,
      sponsored_until: new Date(f.sponsored_until).toISOString(),
      starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : undefined,
      category: f.category.trim() || undefined,
      country: f.country.trim() || undefined,
      city: f.city.trim() || undefined,
      payment_status: f.payment_status,
      agreed_price: f.agreed_price === '' ? undefined : Number(f.agreed_price),
      currency: f.currency.trim() || 'SDG',
      notes: f.notes,
    };

    setSaving(true);
    try {
      const isCreate = modal.mode === 'create';
      const res = await fetch(
        isCreate ? `${API}/api/admin/sponsorships` : `${API}/api/admin/sponsorships/${modal.id}`,
        {
          method: isCreate ? 'POST' : 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(isCreate ? { ...payload, ad_id: f.ad_id.trim() } : payload),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(isCreate
          ? (isRTL ? 'تم إنشاء حملة الرعاية' : 'Sponsorship campaign created')
          : (isRTL ? 'تم حفظ التعديلات' : 'Changes saved'));
        setModal(null);
        load();
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : (isRTL ? 'فشل الحفظ' : 'Save failed'));
      }
    } catch {
      toast.error(isRTL ? 'خطأ في الشبكة' : 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const act = async (r, action) => {
    setActingId(r.sponsorship_id);
    try {
      const res = await fetch(`${API}/api/admin/sponsorships/${r.sponsorship_id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(action === 'activate'
          ? (isRTL ? 'تم تفعيل الحملة' : 'Campaign activated')
          : (isRTL ? 'تم إيقاف الحملة' : 'Campaign paused'));
        load();
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Action failed');
      }
    } catch {
      toast.error(isRTL ? 'خطأ في الشبكة' : 'Network error');
    } finally {
      setActingId(null);
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(isRTL ? 'ar' : 'en', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="admin-sponsorships">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 grid place-items-center">
            <Megaphone size={20} />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-slate-800">
              {isRTL ? 'الرعايات الإعلانية' : 'Sponsorships'}
            </h1>
            <p className="text-xs text-slate-500">
              {isRTL
                ? 'حملات إعلانية مباشرة — الدفع يتم خارج وصلة بالكامل'
                : 'Direct advertiser campaigns — payment happens entirely outside Wassla'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600"
            aria-label="reload" data-testid="admin-sponsorships-reload">
            <RefreshCw size={16} />
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            data-testid="admin-sponsorships-create-btn">
            <Plus size={16} />
            {isRTL ? 'حملة جديدة' : 'New campaign'}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="admin-sponsorships-filters">
        {[
          { v: 'all', en: 'All', ar: 'الكل' },
          { v: 'active', en: 'Active', ar: 'نشطة' },
          { v: 'scheduled', en: 'Scheduled', ar: 'مجدولة' },
          { v: 'paused', en: 'Paused', ar: 'موقوفة' },
          { v: 'expired', en: 'Expired', ar: 'منتهية' },
        ].map((f) => (
          <button key={f.v} onClick={() => setFilter(f.v)}
            data-testid={`admin-sponsorships-filter-${f.v}`}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              filter === f.v ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}>
            {isRTL ? f.ar : f.en}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center text-slate-400">
          <Loader2 className="animate-spin" size={22} />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-500 bg-white rounded-2xl border border-slate-100"
          data-testid="admin-sponsorships-empty">
          {isRTL ? 'لا توجد حملات رعاية بعد' : 'No sponsorship campaigns yet'}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.sponsorship_id}
              className="bg-white rounded-2xl border border-slate-100 p-4 flex flex-col md:flex-row md:items-center gap-3"
              data-testid={`sponsorship-row-${r.sponsorship_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLES[r.status] || 'bg-slate-100 text-slate-600'}`}
                    data-testid="sponsorship-status-chip">
                    {r.status}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-50 text-orange-600">
                    {r.placement === 'homepage' ? (isRTL ? 'الرئيسية' : 'Homepage') : (isRTL ? 'شريط الإعلانات المشابهة' : 'Related carousel')}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">
                    {isRTL ? 'الدفع:' : 'payment:'} {r.payment_status}
                  </span>
                  {!r.listing_visible && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-600"
                      data-testid="sponsorship-listing-unavailable">
                      {isRTL ? 'القائمة غير متاحة — لن تُعرض' : 'Listing unavailable — not rendering'}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 font-semibold text-sm text-slate-800 truncate">
                  {r.advertiser_name} <span className="text-slate-400 font-normal">·</span>{' '}
                  <span className="text-slate-600 font-normal">{r.ad_title || r.ad_id}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {fmtDate(r.starts_at)} → {fmtDate(r.sponsored_until)}
                  {r.city ? ` · ${r.city}` : ''}{r.category ? ` · ${r.category}` : ''}
                </div>
                {r.notes ? <div className="mt-1 text-xs text-slate-400 truncate">{r.notes}</div> : null}
              </div>

              <div className="flex items-center gap-4 text-xs text-slate-600" data-testid="sponsorship-stats">
                <span className="inline-flex items-center gap-1"><Eye size={14} /> {r.stats?.impressions ?? 0}</span>
                <span className="inline-flex items-center gap-1"><MousePointerClick size={14} /> {r.stats?.clicks ?? 0}</span>
              </div>

              <div className="flex items-center gap-2">
                {r.status !== 'expired' && (
                  r.active ? (
                    <button onClick={() => act(r, 'pause')} disabled={actingId === r.sponsorship_id}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-amber-50 text-amber-700 hover:bg-amber-100 text-xs font-semibold"
                      data-testid="sponsorship-pause-btn">
                      <Pause size={14} /> {isRTL ? 'إيقاف' : 'Pause'}
                    </button>
                  ) : (
                    <button onClick={() => act(r, 'activate')} disabled={actingId === r.sponsorship_id}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold"
                      data-testid="sponsorship-activate-btn">
                      <Play size={14} /> {isRTL ? 'تفعيل' : 'Activate'}
                    </button>
                  )
                )}
                <button onClick={() => navigate(`/admin/sponsorship-report/${r.sponsorship_id}`)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-sky-50 text-sky-700 hover:bg-sky-100 text-xs font-semibold"
                  data-testid="sponsorship-report-btn">
                  <FileBarChart size={14} /> {isRTL ? 'تقرير' : 'Report'}
                </button>
                <button onClick={() => openEdit(r)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 text-xs font-semibold"
                  data-testid="sponsorship-edit-btn">
                  <Pencil size={14} /> {isRTL ? 'تعديل' : 'Edit'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[10070] bg-black/40 grid place-items-center p-4" data-testid="sponsorship-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
            <h2 className="text-base font-bold text-slate-800 mb-4">
              {modal.mode === 'create'
                ? (isRTL ? 'حملة رعاية جديدة' : 'New sponsorship campaign')
                : (isRTL ? 'تعديل الحملة' : 'Edit campaign')}
            </h2>

            <div className="space-y-3 text-sm">
              {modal.mode === 'create' && (
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'معرّف الإعلان (Ad ID)' : 'Listing Ad ID'}</span>
                  <input value={modal.form.ad_id} onChange={(e) => setF('ad_id', e.target.value)}
                    placeholder="ad_xxxxxxxxxxxx" dir="ltr"
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                    data-testid="sponsorship-form-ad-id" />
                  <span className="text-[11px] text-slate-400">
                    {isRTL ? 'يجب أن يكون إعلاناً منشوراً وعاماً ومتاحاً' : 'Must be a published, public, available listing'}
                  </span>
                </label>
              )}
              <label className="block">
                <span className="text-xs font-semibold text-slate-600">{isRTL ? 'اسم المعلن / النشاط التجاري' : 'Advertiser / business name'}</span>
                <input value={modal.form.advertiser_name} onChange={(e) => setF('advertiser_name', e.target.value)}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                  data-testid="sponsorship-form-advertiser" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600">{isRTL ? 'الموضع' : 'Placement'}</span>
                <select value={modal.form.placement} onChange={(e) => setF('placement', e.target.value)}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 bg-white"
                  data-testid="sponsorship-form-placement">
                  <option value="homepage">{isRTL ? 'الصفحة الرئيسية' : 'Homepage'}</option>
                  <option value="related_carousel">{isRTL ? 'شريط الإعلانات المشابهة' : 'Related carousel'}</option>
                </select>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'تاريخ البدء' : 'Start date'}</span>
                  <input type="datetime-local" value={modal.form.starts_at} onChange={(e) => setF('starts_at', e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                    data-testid="sponsorship-form-starts" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'تاريخ الانتهاء' : 'Expiry date'}</span>
                  <input type="datetime-local" value={modal.form.sponsored_until} onChange={(e) => setF('sponsored_until', e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                    data-testid="sponsorship-form-until" />
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'الفئة (اختياري)' : 'Category (opt.)'}</span>
                  <input value={modal.form.category} onChange={(e) => setF('category', e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                    data-testid="sponsorship-form-category" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'الدولة (اختياري)' : 'Country (opt.)'}</span>
                  <input value={modal.form.country} onChange={(e) => setF('country', e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                    data-testid="sponsorship-form-country" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'المدينة (اختياري)' : 'City (opt.)'}</span>
                  <input value={modal.form.city} onChange={(e) => setF('city', e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                    data-testid="sponsorship-form-city" />
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'حالة الدفع (داخلي)' : 'Payment status (internal)'}</span>
                  <select value={modal.form.payment_status} onChange={(e) => setF('payment_status', e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 bg-white"
                    data-testid="sponsorship-form-payment-status">
                    <option value="pending">pending</option>
                    <option value="confirmed">confirmed</option>
                    <option value="waived">waived</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'السعر المتفق عليه' : 'Agreed price'}</span>
                  <input type="number" value={modal.form.agreed_price} onChange={(e) => setF('agreed_price', e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                    data-testid="sponsorship-form-price" />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">{isRTL ? 'العملة' : 'Currency'}</span>
                  <input value={modal.form.currency} onChange={(e) => setF('currency', e.target.value)}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2" dir="ltr"
                    data-testid="sponsorship-form-currency" />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600">{isRTL ? 'ملاحظات داخلية' : 'Internal notes'}</span>
                <textarea value={modal.form.notes} onChange={(e) => setF('notes', e.target.value)} rows={2}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2"
                  data-testid="sponsorship-form-notes" />
              </label>
              <p className="text-[11px] text-slate-400">
                {isRTL
                  ? 'الدفع يتم خارج وصلة (تحويل بنكي مباشر مع المعلن). لا يوجد أي دفع داخل التطبيق.'
                  : 'Payment is settled outside Wassla (direct bank arrangement with the advertiser). No in-app payment exists.'}
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setModal(null)} disabled={saving}
                className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-sm font-semibold hover:bg-slate-200"
                data-testid="sponsorship-form-cancel">
                {isRTL ? 'إلغاء' : 'Cancel'}
              </button>
              <button onClick={save} disabled={saving}
                className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
                data-testid="sponsorship-form-save">
                {saving ? <Loader2 className="animate-spin inline" size={14} /> : (isRTL ? 'حفظ' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
